import apiClient from "../utils/apiClient";
import { notifyWorkflowCountChanged } from "../utils/workflowCountEvents";
import {
  WorkflowApprovalDecideRequestDto,
  WorkflowApprovalDecideResultDto,
  WorkflowApprovalStageGroupDto,
  WorkflowApprovalsListResultDto,
  WorkflowApprovalsStatusFilter,
  WorkflowFormSubmitRequestDto,
  WorkflowFormSubmitResultDto,
  WorkflowGraphDto,
  WorkflowInstanceAdvanceRequestDto,
  WorkflowInstanceAdvanceResultDto,
  WorkflowInstanceCancelRequestDto,
  WorkflowInstanceDetailDto,
  WorkflowInstanceFilterDefinitionDto,
  WorkflowInstanceInclude,
  WorkflowInstancesListResultDto,
  WorkflowInstancesQuery,
  WorkflowMyWorkResultDto,
  WorkflowInstanceResumeRequestDto,
  WorkflowInstanceSaveValuesRequestDto,
  WorkflowInstanceSaveValuesResultDto,
  WorkflowInstanceStartRequestDto,
  WorkflowInstanceStartResultDto,
  WorkflowInstanceStateResultDto,
  WorkflowPifApplicantRequestDto,
  WorkflowPifApplicantResultDto,
  WorkflowPifDisplayNamesRequestDto,
  WorkflowPifRateBandDto,
  WorkflowOpenCountDto,
  WorkflowOpenTestRunsDto,
  WorkflowTestRunDiscardResultDto,
  WorkflowCaseStatusDto,
  WorkflowTaskCompleteRequestDto,
  WorkflowTaskCompleteResultDto,
  WorkflowTasksListResultDto,
  WorkflowTasksQuery,
  WorkflowValidationIssueDto,
} from "../types/workflowDto";

// ---------------------------------------------------------------------------
// Workflow engine API (api/workflow/*). Routes are kebab-case and do not match the C# action
// names; see modules/Portal/API_ENDPOINTS.md in the Enterprise API repo.
//
// Every write after start carries the instance's latest `rowVersionTag` — a stale tag answers
// 409, which is how "this case was updated by someone else" surfaces. advance, form/submit and
// approval/decide also take an `idempotencyKey`: generate it once per user action and reuse it
// on a retry, so a double-click or a replay after re-auth is a no-op rather than a second write.
// ---------------------------------------------------------------------------

/** The definition graph. Pass the instance's `workflowDefinitionId` to get its pinned version. */
export const getWorkflowDefinitionGraph = async (definitionId: number): Promise<WorkflowGraphDto> => {
  const response = await apiClient.get<WorkflowGraphDto>("/workflow/definition", {
    params: { id: definitionId },
  });
  return response.data;
};

/**
 * Epic 80430 - the latest PUBLISHED version of a workflow, by its Token, for starting a case.
 * Gated on My Work rather than Library, so a Hiring Supervisor can read it; drafts and archived
 * versions are never returned, and web-service request details are stripped.
 * 404 = the workflow has no published version, 403 = the caller may not start cases.
 */
export const getWorkflowRunDefinitionGraph = async (token: string): Promise<WorkflowGraphDto> => {
  const response = await apiClient.get<WorkflowGraphDto>("/workflow/run-definition", {
    params: { token },
  });
  return response.data;
};

/** Header + saved values (+ approvals/tasks/timeline). Omit `include` for all of them.
 *  404 = deleted or absent, 403 = the caller may not view this case. */
export const getWorkflowInstance = async (
  params: ({ id: number } | { refNo: string }) & { include?: WorkflowInstanceInclude[] },
): Promise<WorkflowInstanceDetailDto> => {
  // Exactly one of id / refNo — the API refuses both or neither with a 400.
  const key = "refNo" in params ? { refNo: params.refNo } : { id: params.id };
  const response = await apiClient.get<WorkflowInstanceDetailDto>("/workflow/instance", {
    params: { ...key, include: params.include?.join(",") || undefined },
  });
  return response.data;
};

/** The two refusals the Run screen renders as its own states rather than as an error alert. */
export type WorkflowRunRefusal = { kind: "not-found" } | { kind: "forbidden" };

export type WorkflowRunLoadResult =
  | { kind: "loaded"; detail: WorkflowInstanceDetailDto; graph: WorkflowGraphDto }
  | WorkflowRunRefusal;

export type WorkflowRunStartResult =
  | { kind: "started"; result: WorkflowInstanceStartResultDto }
  /** 422: the definition exists but isn't runnable - a draft, or a graph without exactly one start. */
  | { kind: "not-runnable" }
  | WorkflowRunRefusal;

/** Read structurally rather than through axios.isAxiosError — see pifWriteResult.ts. */
const httpStatusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

/** 404 / 403 resolve as a refusal; anything else is rethrown so useApi raises its alert. */
const toRunRefusal = (error: unknown): WorkflowRunRefusal => {
  const status = httpStatusOf(error);
  if (status === 404) return { kind: "not-found" };
  if (status === 403) return { kind: "forbidden" };
  throw error;
};

/**
 * Everything the Run screen needs to open a case: the instance and the definition version it is
 * pinned to, which the instance read carries — so a case started from the latest published
 * version runs on the version it was pinned to, even if a newer one has been published since.
 *
 * 404 and 403 are outcomes the screen renders — the "No instance selected" and "You don't have
 * access to this case" states — so they resolve as data instead of raising useApi's alert. Any
 * other failure still rejects, so useApi alerts and the screen offers Retry.
 */
export const loadWorkflowRun = async (instanceId: number): Promise<WorkflowRunLoadResult> => {
  try {
    const detail = await getWorkflowInstance({ id: instanceId });
    // TODO(80430 API): drop this fallback once GET instance returns the pinned graph everywhere.
    // GET definition is gated on Library view, so it still refuses an Instances / My Work user.
    const graph = detail.graph ?? (await getWorkflowDefinitionGraph(detail.instance.workflowDefinitionId));
    return { kind: "loaded", detail, graph };
  } catch (error) {
    return toRunRefusal(error);
  }
};

export type WorkflowRunRefNoResult = { kind: "found"; instanceId: number } | WorkflowRunRefusal;

/**
 * Decision 6.5: resolves the /wf/{RefNo} share link to its case. The same read as opening the case
 * (same 404 / 403 — the link grants nothing), asking only for values so the graph isn't sent twice.
 */
export const resolveWorkflowRunRefNo = async (refNo: string): Promise<WorkflowRunRefNoResult> => {
  try {
    const detail = await getWorkflowInstance({ refNo, include: ["values"] });
    return { kind: "found", instanceId: detail.instance.id };
  } catch (error) {
    return toRunRefusal(error);
  }
};

export type WorkflowDefinitionLoadResult = { kind: "loaded"; graph: WorkflowGraphDto } | WorkflowRunRefusal;

/**
 * Reads a definition before any case exists for it - the Run screen needs to know it is the PIF
 * before starting, because the PIF must not create a case until its HR gate is answered "Yes"
 * (80430 AC2). 404 / 403 resolve as refusals, like loadWorkflowRun.
 */
export const loadWorkflowDefinition = async (definitionId: number): Promise<WorkflowDefinitionLoadResult> => {
  try {
    return { kind: "loaded", graph: await getWorkflowDefinitionGraph(definitionId) };
  } catch (error) {
    return toRunRefusal(error);
  }
};

/** loadWorkflowDefinition for a run started by Token — the latest published version, read without
 *  Library access. 404 here means no published version. */
export const loadWorkflowRunDefinition = async (token: string): Promise<WorkflowDefinitionLoadResult> => {
  try {
    return { kind: "loaded", graph: await getWorkflowRunDefinitionGraph(token) };
  } catch (error) {
    return toRunRefusal(error);
  }
};

export const startWorkflowInstance = async (
  request: WorkflowInstanceStartRequestDto,
): Promise<WorkflowInstanceStartResultDto> => {
  const response = await apiClient.post<WorkflowInstanceStartResultDto>("/workflow/instance/start", request);
  return response.data;
};

/**
 * Starts a case for the Run screen. 404 = no such definition; 422 = it exists but can't run (only a
 * published version runs, except a Builder test run of a draft; either way it needs exactly one
 * start stage - sProc_System_WorkflowInstance_Start_I); 403 = the caller may not start cases, or
 * asked for a test run without Builder edit. Send the same `idempotencyKey` on a retry —
 * the server then replays the original case instead of creating a second one.
 */
export const startWorkflowRun = async (request: {
  definitionId: number;
  idempotencyKey: string;
  /** Epic 80431 - a Builder test run. */
  isTest?: boolean;
  /** Epic 80431 - discard the caller's open test runs of this workflow first (the prompt's Yes). */
  discardOpenTestRuns?: boolean;
}): Promise<WorkflowRunStartResult> => {
  try {
    const result = await startWorkflowInstance(request);
    notifyWorkflowCountChanged();
    return { kind: "started", result };
  } catch (error) {
    // Only here: on the other calls a 422 is a validation failure, not "this can't run".
    if (httpStatusOf(error) === 422) return { kind: "not-runnable" };
    return toRunRefusal(error);
  }
};

export const saveWorkflowInstanceValues = async (
  request: WorkflowInstanceSaveValuesRequestDto,
): Promise<WorkflowInstanceSaveValuesResultDto> => {
  const response = await apiClient.post<WorkflowInstanceSaveValuesResultDto>(
    "/workflow/instance/save-values",
    request,
  );
  return response.data;
};

export type WorkflowRunSaveResult = { kind: "saved"; rowVersionTag: string } | { kind: "conflict" };

/** Draft-saves the current stage's answers. 409 = someone else moved or edited the case since it
 *  was loaded (a stale `rowVersionTag`), which the screen renders as its conflict banner. */
export const saveWorkflowRunValues = async (
  request: WorkflowInstanceSaveValuesRequestDto,
): Promise<WorkflowRunSaveResult> => {
  try {
    const result = await saveWorkflowInstanceValues(request);
    return { kind: "saved", rowVersionTag: result.rowVersionTag };
  } catch (error) {
    if (httpStatusOf(error) === 409) return { kind: "conflict" };
    throw error;
  }
};

export type WorkflowPifApplicantResult =
  | { kind: "saved"; rowVersionTag: string }
  | { kind: "conflict" }
  /** 422 — `code` is the server's reason (NOT_A_NEW_HIRE, INVALID_SSN, …). */
  | { kind: "refused"; code: string }
  | WorkflowRunRefusal;

/**
 * Saves a new hire's SSN to their PostIF applicant record (decision P4). Called once, right after
 * pif-1's submit; the server links the case to the record itself. The response carries only a
 * fresh `rowVersionTag` — never the SSN or the applicant id.
 */
export const saveWorkflowPifApplicant = async (
  request: WorkflowPifApplicantRequestDto,
): Promise<WorkflowPifApplicantResult> => {
  try {
    const response = await apiClient.post<WorkflowPifApplicantResultDto>("/workflow/pif/applicant", request);
    return { kind: "saved", rowVersionTag: response.data.rowVersionTag };
  } catch (error) {
    const status = httpStatusOf(error);
    if (status === 409) return { kind: "conflict" };
    if (status === 422) {
      const body = (error as { response?: { data?: { error?: string } } }).response?.data;
      return { kind: "refused", code: body?.error ?? "VALIDATION_FAILED" };
    }
    return toRunRefusal(error);
  }
};

export type WorkflowPifDisplayNamesResult = { kind: "saved" } | { kind: "not-saved" };

/**
 * Has the server write pif-3's display names from the IDs the case saved (approvers sign off on
 * these, so the browser never supplies them). Every refusal resolves quietly as "not saved": the
 * screen simply tries again on the case's next load, and an approver who opens the case never
 * sees an error for a step that isn't theirs. Only a network failure raises useApi's alert.
 */
export const saveWorkflowPifDisplayNames = async (
  request: WorkflowPifDisplayNamesRequestDto,
): Promise<WorkflowPifDisplayNamesResult> => {
  try {
    await apiClient.post("/workflow/pif/display-names", request);
    return { kind: "saved" };
  } catch (error) {
    if (httpStatusOf(error) !== undefined) return { kind: "not-saved" };
    throw error;
  }
};

/** A case's light status, for the Run screen's wait on a web-service step (Epic 80431). Null on any
 *  failure: the caller treats that as "can't tell" and stops, rather than raising an alert per poll. */
export const getWorkflowInstanceStatus = async (instanceId: number): Promise<WorkflowCaseStatusDto | null> => {
  try {
    const response = await apiClient.get<WorkflowCaseStatusDto>("/workflow/instance/status", { params: { id: instanceId } });
    return response.data;
  } catch {
    return null;
  }
};

/** The approvers' rate-band flag (Epic 80430). Advisory: any refusal or failure resolves null, so
 *  the approval screen simply shows no pill and the decision is never held up by it. */
export const getWorkflowPifRateBand = async (instanceId: number): Promise<WorkflowPifRateBandDto | null> => {
  try {
    const response = await apiClient.get<WorkflowPifRateBandDto>("/workflow/pif/rate-band", { params: { instanceId } });
    return response.data;
  } catch {
    return null;
  }
};

/** Runs the current stage's handler and follows the matched transition. There is no
 *  `fromStageId` — the server advances whichever stage is current. */
export const advanceWorkflowInstance = async (
  request: WorkflowInstanceAdvanceRequestDto,
): Promise<WorkflowInstanceAdvanceResultDto> => {
  const response = await apiClient.post<WorkflowInstanceAdvanceResultDto>("/workflow/instance/advance", request);
  return response.data;
};

export const cancelWorkflowInstance = async (
  request: WorkflowInstanceCancelRequestDto,
): Promise<WorkflowInstanceStateResultDto> => {
  const response = await apiClient.post<WorkflowInstanceStateResultDto>("/workflow/instance/cancel", request);
  return response.data;
};

/** Ends a pause early. The server only lets the pause owner do this. */
export const resumeWorkflowInstance = async (
  request: WorkflowInstanceResumeRequestDto,
): Promise<WorkflowInstanceStateResultDto> => {
  const response = await apiClient.post<WorkflowInstanceStateResultDto>("/workflow/instance/resume", request);
  return response.data;
};

/** Submits a form stage. The server re-validates; 422 carries `issues`. */
export const submitWorkflowForm = async (
  request: WorkflowFormSubmitRequestDto,
): Promise<WorkflowFormSubmitResultDto> => {
  const response = await apiClient.post<WorkflowFormSubmitResultDto>("/workflow/form/submit", request);
  return response.data;
};

export type WorkflowRunSubmitResult =
  | { kind: "submitted"; result: WorkflowFormSubmitResultDto }
  | { kind: "invalid"; issues: WorkflowValidationIssueDto[] }
  | { kind: "conflict" }
  | WorkflowRunRefusal;

/**
 * Continue on a form stage: validates, commits the answers and advances in one call, so no
 * separate save-values or advance is needed. 422 = per-field `issues[]` for the screen to place on
 * its fields; 409 = stale `rowVersionTag` or the case moved on; 403 = not this stage's actor.
 */
export const submitWorkflowRunForm = async (
  request: WorkflowFormSubmitRequestDto,
): Promise<WorkflowRunSubmitResult> => {
  try {
    const result = await submitWorkflowForm(request);
    notifyWorkflowCountChanged();
    return { kind: "submitted", result };
  } catch (error) {
    const status = httpStatusOf(error);
    if (status === 409) return { kind: "conflict" };
    if (status === 422) {
      const body = (error as { response?: { data?: { issues?: WorkflowValidationIssueDto[] } } }).response?.data;
      return { kind: "invalid", issues: body?.issues ?? [] };
    }
    return toRunRefusal(error);
  }
};

export const getWorkflowApprovals = async (params: {
  instanceId: number;
  stageId?: number;
  status?: WorkflowApprovalsStatusFilter;
}): Promise<WorkflowApprovalsListResultDto> => {
  const response = await apiClient.get<WorkflowApprovalsListResultDto>("/workflow/approvals", { params });
  return response.data;
};

/** Stamps accept / reject / cancel. The server records the IP and DecidedOn — never send them. */
export const decideWorkflowApproval = async (
  request: WorkflowApprovalDecideRequestDto,
): Promise<WorkflowApprovalDecideResultDto> => {
  const response = await apiClient.post<WorkflowApprovalDecideResultDto>("/workflow/approval/decide", request);
  return response.data;
};

/** The current approval stage's group (its approvers and their decisions). `group` is null when
 *  the case has no approval rows for that stage — a mis-configured stage with no approver
 *  resolved. Wrapped so a caller going through useApi can tell that apart from a failed read. */
export const loadWorkflowStageApprovals = async (params: {
  instanceId: number;
  stageId: number;
}): Promise<{ group: WorkflowApprovalStageGroupDto | null }> => {
  const result = await getWorkflowApprovals({ ...params, status: "all" });
  return { group: result.stages.find((stage) => stage.stageId === params.stageId) ?? null };
};

export type WorkflowRunDecideResult =
  | { kind: "decided"; result: WorkflowApprovalDecideResultDto }
  | { kind: "conflict" }
  /** 403, or 422 DECISION_NOT_PERMITTED: the caller is not this stage's resolved approver. */
  | { kind: "not-permitted" }
  /** 422 UNRESOLVED_APPROVER: no approver could be resolved for the stage. */
  | { kind: "unresolved" }
  | { kind: "not-found" };

/**
 * Accept / reject / cancel on a real approval stage. 409 = stale tag, this row already decided,
 * or the stage already resolved (the loser of two concurrent decisions) — reload, never retry.
 */
export const decideWorkflowRunApproval = async (
  request: WorkflowApprovalDecideRequestDto,
): Promise<WorkflowRunDecideResult> => {
  try {
    const result = await decideWorkflowApproval(request);
    notifyWorkflowCountChanged();
    return { kind: "decided", result };
  } catch (error) {
    const status = httpStatusOf(error);
    if (status === 409) return { kind: "conflict" };
    if (status === 403) return { kind: "not-permitted" };
    if (status === 404) return { kind: "not-found" };
    if (status === 422) {
      const code = (error as { response?: { data?: { error?: string } } }).response?.data?.error;
      return code === "UNRESOLVED_APPROVER" ? { kind: "unresolved" } : { kind: "not-permitted" };
    }
    throw error;
  }
};


/** How a Task / Pause / Cancel action on a real case came back, when it did not simply succeed. */
export type WorkflowCaseActionRefusal =
  /** 409: stale tag, or the case moved on since it was loaded. */
  | { kind: "conflict" }
  /** 403: not this user's to do — not the pause owner, not the task's assignee. */
  | { kind: "forbidden" }
  | { kind: "not-found" }
  /** 422: the engine refused it as invalid; `message` is its own sentence when it gave one. */
  | { kind: "invalid"; message: string | null };

export type WorkflowCaseActionResult<T> = { kind: "done"; result: T } | WorkflowCaseActionRefusal;

const toCaseActionRefusal = (error: unknown): WorkflowCaseActionRefusal => {
  const status = httpStatusOf(error);
  if (status === 409) return { kind: "conflict" };
  if (status === 403) return { kind: "forbidden" };
  if (status === 404) return { kind: "not-found" };
  if (status === 422) {
    const body = (error as { response?: { data?: { message?: string } } }).response?.data;
    return { kind: "invalid", message: body?.message ?? null };
  }
  throw error;
};

const asCaseAction = async <T,>(call: () => Promise<T>): Promise<WorkflowCaseActionResult<T>> => {
  try {
    const result = await call();
    // Cancel, resume and task completion can each finish a case or move it on.
    notifyWorkflowCountChanged();
    return { kind: "done", result };
  } catch (error) {
    return toCaseActionRefusal(error);
  }
};

/** Cancel the running case (terminal). The engine requires a reason. */
export const cancelWorkflowRun = (request: WorkflowInstanceCancelRequestDto) =>
  asCaseAction(() => cancelWorkflowInstance(request));

/** Ends a pause early. 403 = the caller is not the pause owner. */
export const resumeWorkflowRun = (request: WorkflowInstanceResumeRequestDto) =>
  asCaseAction(() => resumeWorkflowInstance(request));

/** Completes one checklist task; `stageAdvanced` = that was the last one and the case moved on. */
export const completeWorkflowRunTask = (request: WorkflowTaskCompleteRequestDto) =>
  asCaseAction(() => completeWorkflowTask(request));

export const getWorkflowTasks = async (query: WorkflowTasksQuery): Promise<WorkflowTasksListResultDto> => {
  const response = await apiClient.get<WorkflowTasksListResultDto>("/workflow/tasks", { params: query });
  return response.data;
};

export const completeWorkflowTask = async (
  request: WorkflowTaskCompleteRequestDto,
): Promise<WorkflowTaskCompleteResultDto> => {
  const response = await apiClient.post<WorkflowTaskCompleteResultDto>("/workflow/task/complete", request);
  return response.data;
};

// ---------------------------------------------------------------------------
// Epic 80431 - Instances & My Work. Scope is decided by the server from the caller's Portal roles
// (scope=auto); nothing here filters by role on the client.
// ---------------------------------------------------------------------------

/** One page of cases. `page` is 1-based; the server caps pageSize at 100. */
export const getWorkflowInstances = async (
  query: WorkflowInstancesQuery,
): Promise<WorkflowInstancesListResultDto> => {
  const response = await apiClient.get<WorkflowInstancesListResultDto>("/workflow/instances", {
    params: { scope: "auto", ...query, search: query.search?.trim() || undefined },
  });
  return response.data;
};

export type WorkflowInstancesLoadResult =
  | { kind: "loaded"; page: WorkflowInstancesListResultDto }
  | { kind: "forbidden" };

/**
 * The Instances table's read. 403 (no Instances permission, or a scope the caller may not ask for)
 * is a state the table renders - "You don't have access to instances." with no rows - so it
 * resolves as data. Anything else rejects, so useApi raises its alert and the table offers Retry.
 */
export const loadWorkflowInstances = async (
  query: WorkflowInstancesQuery,
): Promise<WorkflowInstancesLoadResult> => {
  try {
    return { kind: "loaded", page: await getWorkflowInstances(query) };
  } catch (error) {
    if (httpStatusOf(error) === 403) return { kind: "forbidden" };
    throw error;
  }
};

/** The workflow filter's options - every family, archived ones left out. Instances-gated. */
export const getWorkflowInstanceFilterDefinitions = async (): Promise<WorkflowInstanceFilterDefinitionDto[]> => {
  const response = await apiClient.get<WorkflowInstanceFilterDefinitionDto[]>("/workflow/instances/definitions");
  return response.data;
};

/** Everything waiting on the caller, oldest first. My Work-gated. */
export const getWorkflowMyWork = async (
  params: { page?: number; pageSize?: number } = {},
): Promise<WorkflowMyWorkResultDto> => {
  const response = await apiClient.get<WorkflowMyWorkResultDto>("/workflow/my-work", { params });
  return response.data;
};

/** The sidebar badge: one count-only read (Pooja Pujara on 80430, 2026-10-16), not the list path. */
export const getWorkflowInProgressCount = async (): Promise<number> => {
  const response = await apiClient.get<WorkflowOpenCountDto>("/workflow/instances/open-count");
  return response.data.total;
};

/** Epic 80431 - the caller's unfinished Builder test runs of the workflow (any version). Builder edit. */
export const getWorkflowOpenTestRuns = async (definitionId: number): Promise<number> => {
  const response = await apiClient.get<WorkflowOpenTestRunsDto>("/workflow/instances/open-test-runs", {
    params: { definitionId },
  });
  return response.data.total;
};

/**
 * Epic 80431 - "Discard test run": cancels a Builder test run and everything open on it. 403 =
 * someone else's run and not a Builder editor; 409 = already finished; 422 = a real case.
 */
export const discardWorkflowTestRun = async (instanceId: number): Promise<WorkflowTestRunDiscardResultDto> => {
  const response = await apiClient.post<WorkflowTestRunDiscardResultDto>("/workflow/instance/discard-test", {
    instanceId,
  });
  notifyWorkflowCountChanged();
  return response.data;
};
